/*
  luje-filter-drawer.js — deferred-apply + keyboard support for the collection
  filter drawer.

  WHAT THE THEME DOES, measured rather than assumed
  assets/2.0-filters.min.js binds a DEBOUNCED `input` listener on
  #CollectionFiltersForm:

      filtersForm.addEventListener("input", debouncedOnSubmit)   // 500ms
      onSubmitHandler(e) { e.preventDefault();
        const n = new URLSearchParams(new FormData(filtersForm)).toString();
        this.renderPage(n); }

  so ticking one value re-renders the grid ~500ms later. renderPage() also
  replaces the sidebar markup and calls history.pushState. That is why:
    - the grid changed under the shopper on every click,
    - the drawer shut each time (its "open" state is only an inline
      style="display:block", which the replacement markup does not carry),
    - several values could not be chosen before it all reran.

  (An earlier reading of this as a full page navigation was wrong — the
  "navigation" was the pushState. It is AJAX, which is why the observer below
  is the right tool.)

  WHAT THIS DOES
  1. Swallows user-initiated `input`/`change` events inside the form during the
     capture phase, so the theme's debounced listener never sees them. The
     checkbox still toggles; nothing is applied.
  2. Shows the Apply bar rendered by snippets/storefront-filters.liquid, with a
     running count of pending selections.
  3. Apply re-emits a single `input` event that IS allowed through, so the
     theme's own handler does the work — same URL building, same AJAX render,
     same pushState, same counts. No reimplementation of Shopify's filtering.
  4. Apply commits AND closes, so the results are visible immediately. Ticking
     values leaves the drawer open; only Apply and the close control commit.
     Tab is trapped inside, Escape closes, and the close control — a <div> in
     the theme — is made operable.

  Progressive enhancement: with JS off, the Apply bar stays hidden and the
  theme's apply-on-change behaviour is exactly as it ships.
*/
(function () {
  'use strict';

  var SIDEBAR = '.sf__sidebar';
  var OPENER = '.sf__sidebar-open';
  var CLOSER = '.sf__sidebar-close';
  var FORM = '#CollectionFiltersForm';

  var KEY = 'luje:filters-open';
  var lastOpener = null;
  var applying = false;   // true only while we deliberately let an event pass
  var pending = false;    // shopper has changed something since the last apply

  function remember(v) {
    try { v ? sessionStorage.setItem(KEY, '1') : sessionStorage.removeItem(KEY); } catch (e) {}
  }
  function remembered() {
    try { return sessionStorage.getItem(KEY) === '1'; } catch (e) { return false; }
  }

  function sidebar() { return document.querySelector(SIDEBAR); }
  function form() { return document.querySelector(FORM); }

  function isOpen(el) {
    return !!el && el.style.display !== 'none' && getComputedStyle(el).display !== 'none';
  }

  /* Re-opening by setting display:block is NOT enough and looks broken: the
     overlay appears (page dims) while the panel itself stays translated off
     screen, because the theme animates .sf__sidebar-content separately. Caught
     on a screenshot — every DOM assertion said "open".
     So drive the theme's own opener instead and let it run its full sequence;
     only fall back to the manual style if that control is missing. */
  var reopening = false;

  function openDrawer(el) {
    if (!el) return;
    if (isOpen(el)) { markDialog(el); enhance(el); return; }

    var opener = document.querySelector(OPENER);
    if (opener && !reopening) {
      reopening = true;
      opener.click();
      setTimeout(function () {
        reopening = false;
        var fresh = sidebar();
        if (fresh) { markDialog(fresh); enhance(fresh); }
      }, 60);
      return;
    }

    el.style.display = 'block';
    el.style.setProperty('--tw-bg-opacity', '0.5');
    markDialog(el);
    enhance(el);
  }

  function markDialog(el) {
    el.removeAttribute('aria-hidden');
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
  }

  function closeDrawer(el) {
    if (!el) return;
    /* "or close it and have the changes applied" — a pending selection is
       committed on close rather than silently thrown away. keepOpen=true
       because this function is itself the close. */
    if (pending) applyNow(true);
    el.style.display = 'none';
    remember(false);
    if (lastOpener && document.contains(lastOpener)) lastOpener.focus();
  }

  /* ---- deferred apply ---------------------------------------------------- */

  function countSelected(f) {
    if (!f) return 0;
    return f.querySelectorAll('input[type="checkbox"]:checked').length;
  }

  function bar() { return document.querySelector('[data-luje-apply-bar]'); }

  function refreshBar() {
    var b = bar();
    if (!b) return;
    b.hidden = false;
    var n = countSelected(form());
    var label = b.querySelector('[data-luje-apply-count]');
    if (label) {
      var tpl = label.getAttribute('data-template') || '999';
      label.textContent = n ? tpl.replace('999', n) : '';
    }
    b.classList.toggle('is-dirty', pending);
    var apply = b.querySelector('[data-luje-apply]');
    if (apply) apply.disabled = !pending;
  }

  /* Apply COMMITS and CLOSES.

     It used to apply and stay open, which looked broken: the re-render swaps
     the sidebar, this script re-opened the replacement, and the backdrop came
     up before the panel had slid in — so the page sat dark with no panel until
     you clicked somewhere to dismiss it. Timing around that race is fragile;
     closing on apply removes it, and matches what Apply means anyway — you
     have committed, so you want to see the results.

     Ticking filters still leaves the drawer open. Only Apply (and the close
     control) commit. */
  var closing = false;

  function applyNow(keepOpen) {
    var f = form();
    if (!f) return;
    pending = false;
    /* 24 Aug 2026 (performance audit): committing used to dispatch 'input'
       and let the theme's debounced listener pick it up — but that debounce
       is 500ms (sized for typing in the price fields), so every Apply sat
       half a second doing nothing before the fetch even started (measured:
       1.13s tap-to-grid; 0.50s with the direct call). onSubmitHandler is the
       exact function the debounce would call — public on the
       <collection-filters-form> element, and it only reads the form via
       this.domNodes, so a stub event is fine. The dispatch remains as a
       fallback in case the element's API ever changes. */
    var cf = f.closest('collection-filters-form');
    if (cf && typeof cf.onSubmitHandler === 'function') {
      cf.onSubmitHandler({ preventDefault: function () {} });
    } else {
      applying = true;
      f.dispatchEvent(new Event('input', { bubbles: true }));
      setTimeout(function () { applying = false; }, 0);
    }
    refreshBar();
    if (!keepOpen) dismiss();
  }

  /* Close through the theme's own control so its backdrop teardown and panel
     animation both run. remember(false) first, so the re-render that Apply
     triggers does not bring the drawer back. */
  function dismiss() {
    remember(false);
    var el = sidebar();
    if (!el || closing) return;
    closing = true;
    var c = el.querySelector(CLOSER);
    if (c) {
      c.click();
    } else {
      el.style.display = 'none';
      el.removeAttribute('aria-modal');
    }
    setTimeout(function () { closing = false; }, 100);
    if (lastOpener && document.contains(lastOpener)) lastOpener.focus();
  }

  function clearAll() {
    var f = form();
    if (!f) return;
    [].forEach.call(f.querySelectorAll('input[type="checkbox"]:checked'), function (c) { c.checked = false; });
    [].forEach.call(f.querySelectorAll('input[type="number"],input[type="text"]'), function (i) {
      if (/price/i.test(i.name || '')) i.value = '';
    });
    /* Local reset only — deliberately does NOT apply. Applying here would
       trigger a render and the same reopen race, and it is more useful to let
       the shopper re-pick and then commit once. Apply (or closing) commits. */
    pending = true;
    refreshBar();
  }

  /* The price-range slider (storefront-filters.liquid's price_range case) is
     a <price-range> custom element defined in 2.0-filters.min.js. Dragging a
     handle or typing a value fires a native "input" event that bubbles up
     from the actual <input>, through <price-range>, up to this form —
     <price-range>'s own constructor binds a delegated "input" listener on
     itself that moves the paired text/range control and repaints the
     --from/--to CSS custom properties driving the track fill.

     Capture-phase interception at the FORM (an ANCESTOR of <price-range>)
     runs during the top-down capture pass, before the event ever reaches
     <price-range> at all — stopImmediatePropagation() here kills it before
     <price-range>'s own bubble-phase listener gets a chance to run, same as
     it kills the theme's debounced submit. The checkbox/text-input cases
     don't notice, because their own visible state ("checked", the typed
     digits) is native browser behavior, not driven by a listener that can be
     preempted this way — "the checkbox still toggles" is only true because
     nothing OTHER than native behavior was needed. The slider's fill/synced
     pair only exist because JS put them there, so killing the event before
     that JS runs means dragging visibly does nothing: the raw slider value
     moves (native, unstoppable), everything downstream of it doesn't.

     There is no reliable way to let the event reach <price-range>'s own
     listener but still stop it before the form's — DOM propagation is an
     all-or-nothing cutoff from wherever stopPropagation is called, and
     re-dispatching a synthetic event to replay just that one hop is exactly
     as fragile as the script-load-order issue this capture-phase approach
     was chosen to avoid in the first place (see below). So this replicates
     <price-range>'s own sync inline, from its decompiled constructor logic,
     before the event is swallowed — same effect, no dependence on which
     script's listener happens to attach to the form first. */
  function syncPriceRange(target) {
    var pr = target.closest && target.closest('price-range');
    if (!pr) return;
    var min = pr.querySelector('[data-type="min-range"]');
    var max = pr.querySelector('[data-type="max-range"]');
    var minInput = pr.querySelector('[data-type="min-input"]');
    var maxInput = pr.querySelector('[data-type="max-input"]');
    if (!min || !max || !minInput || !maxInput) return;
    var priceMax = Number(pr.dataset.priceMax);
    var priceGap = Number(pr.dataset.priceGap);

    switch (target.dataset.type) {
      case 'min-range':
        if (Number(max.value) - Number(target.value) >= priceGap) {
          minInput.value = target.value;
        } else {
          target.value = Number(max.value) - priceGap;
        }
        break;
      case 'max-range':
        if (Number(target.value) - Number(min.value) >= priceGap) {
          maxInput.value = target.value;
        } else {
          target.value = Number(min.value) + priceGap;
        }
        break;
      default: /* min-input or max-input: a typed value */
        if (Number(target.value) - priceMax > 0) target.value = priceMax;
        (target.dataset.type === 'min-input' ? min : max).value = target.value;
    }
    pr.style.setProperty('--from', (100 * Number(min.value) / priceMax) + '%');
    pr.style.setProperty('--to', (100 * Number(max.value) / priceMax) + '%');
  }

  /* Swallow the theme's auto-apply. Capture phase on the form means we run
     before its own listener and stopPropagation prevents it ever firing.

     Sort-by is exempt: the sort select lives inside this same form (see
     storefront-filters.liquid's hidden data-form-sorting duplicate), but
     2.0-filters.min.js's handleSorting() applies it by dispatching "input"
     directly on the form element itself (filtersForm.dispatchEvent(new
     Event("input"))) rather than letting a real control event bubble up.
     That is the one case where e.target === the form, so it is the reliable
     signal to distinguish "sort just changed" from "a filter checkbox/range
     input changed" (whose events always target the control, not the form).
     Sorting is a single immediate choice, not a multi-select batch like the
     filters, so it should never be held behind Apply. */
  /* Sort commit (handleSorting dispatches 'input' on the form itself).
     24 Aug 2026 (performance audit): run it immediately instead of letting
     the theme's 500ms typing-debounce delay it. This must be a DOCUMENT-level
     capture listener: when an event's target IS the form, listeners on the
     form fire in registration order regardless of the capture flag, and the
     theme registers its debounced listener first — so intercepting on the
     form could not reliably beat it. Document capture always runs before any
     target listener, so stopPropagation here is deterministic. If the
     element's API is unavailable, the event passes through untouched and the
     theme's debounced listener handles it as before. */
  document.addEventListener('input', function (e) {
    var f = form();
    if (!f || applying || e.target !== f) return;
    var cf = f.closest('collection-filters-form');
    if (cf && typeof cf.onSubmitHandler === 'function') {
      e.stopPropagation();
      e.stopImmediatePropagation();
      cf.onSubmitHandler({ preventDefault: function () {} });
    }
  }, true);

  function intercept(e) {
    var f = form();
    if (!f || applying) return;
    if (e.target === f) return;
    if (!f.contains(e.target)) return;
    syncPriceRange(e.target);
    e.stopPropagation();
    e.stopImmediatePropagation();
    pending = true;
    refreshBar();
  }

  /* ---- keyboard / dialog ------------------------------------------------- */

  function enhance(el) {
    var c = el.querySelector(CLOSER);
    if (c && !c.dataset.lujeUpgraded) {
      c.dataset.lujeUpgraded = '1';
      c.setAttribute('role', 'button');
      c.setAttribute('tabindex', '0');
      if (!c.getAttribute('aria-label')) c.setAttribute('aria-label', 'Close filters');
      c.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') { e.preventDefault(); c.click(); }
      });
    }
    var f = el.querySelector(FORM) || form();
    if (f && !f.dataset.lujeIntercepted) {
      f.dataset.lujeIntercepted = '1';
      f.addEventListener('input', intercept, true);
      f.addEventListener('change', intercept, true);
    }
    refreshBar();
  }

  function focusables(el) {
    return [].slice
      .call(el.querySelectorAll('a[href],button:not([disabled]),input,select,textarea,[tabindex]:not([tabindex="-1"]),[role="button"]'))
      .filter(function (n) {
        if (n.disabled) return false;
        var s = getComputedStyle(n);
        if (s.visibility === 'hidden' || s.display === 'none') return false;
        /* filter checkboxes are deliberately 1x1 and clipped, so a zero-area
           test would wrongly discard them */
        return true;
      });
  }

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t.closest) return;

    if (t.closest('[data-luje-apply]')) { e.preventDefault(); applyNow(); return; }
    if (t.closest('[data-luje-clear]')) { e.preventDefault(); clearAll(); return; }

    var opener = t.closest(OPENER);
    if (opener) {
      remember(true);
      lastOpener = opener;
      setTimeout(function () { openDrawer(sidebar()); }, 0);
      return;
    }
    if (t.closest(CLOSER)) {
      var el = sidebar();
      /* keepOpen=true: the theme's own handler is already closing the drawer,
         so committing must not call dismiss() and click this control again. */
      if (pending) applyNow(true);
      remember(false);
      if (el) el.removeAttribute('aria-modal');
    }
  }, true);

  document.addEventListener('keydown', function (e) {
    var el = sidebar();
    if (!isOpen(el)) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      var c = el.querySelector(CLOSER);
      if (c) c.click(); else closeDrawer(el);
      return;
    }
    if (e.key !== 'Tab') return;

    var f = focusables(el);
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];

    if (!el.contains(document.activeElement)) { e.preventDefault(); first.focus(); return; }
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  /* renderPage() replaces the sidebar, taking the inline "open" style and our
     listeners with it. Re-apply both on every swap. */
  function watch() {
    new MutationObserver(function (muts) {
      var swapped = muts.some(function (m) {
        return [].slice.call(m.addedNodes).some(function (n) {
          return n.nodeType === 1 && n.querySelector && (n.matches(SIDEBAR) || n.querySelector(SIDEBAR) || n.querySelector(FORM));
        });
      });
      if (!swapped) return;
      var fresh = sidebar();
      if (!fresh) return;
      enhance(fresh);
      if (remembered() && !isOpen(fresh)) openDrawer(fresh);
    }).observe(document.body, { childList: true, subtree: true });
  }

  function start() {
    var el = sidebar();
    if (el) {
      enhance(el);
      if (remembered() && !isOpen(el)) openDrawer(el);
    }
    refreshBar();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { start(); watch(); });
  } else {
    start(); watch();
  }
})();
